import { inject, Injectable, NgZone } from '@angular/core';
import { defer, Observable, of, ReplaySubject, throwError, timer } from 'rxjs';
import { catchError, first, map, shareReplay, timeout } from 'rxjs/operators';

import { DOCUMENT_TOKEN, WINDOW } from '../tokens/injection-tokens';
import { environment } from '../../../environments/environment';

interface TurnstileInstance {
  render(container: string | HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
  getResponse?(widgetId: string): string | undefined;
  isExpired?(widgetId: string): boolean;
}

interface WindowWithTurnstile extends Window {
  turnstile?: TurnstileInstance;
  __turnstileOnload?: () => void;
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': (code?: string) => void;
  'expired-callback': () => void;
  'timeout-callback'?: () => void;
  'unsupported-callback'?: () => void;
  size: 'invisible' | 'compact' | 'normal' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
  retry?: 'auto' | 'never';
  'refresh-expired'?: 'auto' | 'manual' | 'never';
}

const TURNSTILE_CB_NAME = '__turnstileOnload';
const TURNSTILE_SCRIPT_URL = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${TURNSTILE_CB_NAME}`;
const CONTAINER_ID = 'turnstile-container';
const TOKEN_TIMEOUT_MS = 30_000;
const DUMMY_SITE_KEY = '1x00000000000000000000AA';
const MOCK_DELAY_MIN_MS = 200;
const MOCK_DELAY_RANGE_MS = 1500;

@Injectable({ providedIn: 'root' })
export class TurnstileService {
  private readonly window = inject<Window>(WINDOW);
  private readonly document = inject<Document>(DOCUMENT_TOKEN);
  private readonly ngZone = inject(NgZone);

  private widgetId: string | null = null;
  private activeSubject: ReplaySubject<string> | null = null;
  private initialized = false;
  private scriptLoaded = false;
  private scriptLoading: Promise<void> | null = null;

  // Shared observable for an in-flight challenge. While a challenge is
  // running, every getToken() caller subscribes to the same stream so we
  // trigger exactly one widget reset per typing burst. Cleared synchronously
  // the moment the token (or error) is delivered, so the next call starts
  // a fresh challenge.
  private tokenInFlight$: Observable<string> | null = null;

  getToken(): Observable<string> {
    // defer ensures retry re-entering this observable (after an error) runs
    // the factory again — otherwise retry would just resubscribe to the
    // cached errored shareReplay and never kick off a new challenge.
    return defer(() => this.acquireToken());
  }

  private acquireToken(): Observable<string> {
    if (!environment.turnstileSiteKey) {
      return of('');
    }

    if (environment.turnstileSiteKey === DUMMY_SITE_KEY) {
      // Simulate Cloudflare challenge latency so local dev exercises the same
      // async race conditions (autocomplete cancellation, token sharing)
      // that occur in prod instead of resolving synchronously.
      const delay = MOCK_DELAY_MIN_MS + Math.random() * MOCK_DELAY_RANGE_MS;
      return timer(delay).pipe(
        map(() => `dev-token-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
      );
    }

    if (this.tokenInFlight$) {
      return this.tokenInFlight$;
    }

    const subject = new ReplaySubject<string>(1);
    this.activeSubject = subject;

    this.tokenInFlight$ = subject.asObservable().pipe(
      first(),
      timeout(TOKEN_TIMEOUT_MS),
      catchError((err: unknown) => {
        if (this.activeSubject === subject) this.activeSubject = null;
        this.tokenInFlight$ = null;
        return throwError(() => err);
      }),
      // refCount:false keeps the source alive even when every upstream
      // subscriber has been cancelled (rapid-typing switchMap). When the
      // next keystroke's autocomplete subscribes, it piggybacks on the
      // already-running challenge instead of queueing a new one.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.kickOffChallenge(subject);

    return this.tokenInFlight$;
  }

  private kickOffChallenge(subject: ReplaySubject<string>): void {
    this.ensureScriptLoaded()
      .then(() => {
        if (!this.initialized) {
          this.renderWidget();
        } else {
          this.resetWidget();
        }
      })
      .catch(() => {
        subject.error(new Error('Turnstile script failed to load'));
        if (this.activeSubject === subject) this.activeSubject = null;
        this.tokenInFlight$ = null;
      });
  }

  private ensureScriptLoaded(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    if (this.scriptLoading) {
      return this.scriptLoading;
    }

    this.scriptLoading = new Promise<void>((resolve, reject) => {
      this.ensureContainer();

      const win = this.window as WindowWithTurnstile;
      win[TURNSTILE_CB_NAME] = () => {
        this.scriptLoaded = true;
        resolve();
      };

      const script = this.document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onerror = () => {
        this.scriptLoading = null;
        reject(new Error('Failed to load Turnstile script'));
      };
      this.document.head.appendChild(script);
    });

    return this.scriptLoading;
  }

  private ensureContainer(): void {
    if (this.document.getElementById(CONTAINER_ID)) return;

    const container = this.document.createElement('div');
    container.id = CONTAINER_ID;
    container.style.cssText =
      'position: fixed; bottom: 0; left: 0; width: 1px; height: 1px; overflow: hidden;';
    this.document.body.appendChild(container);
  }

  private renderWidget(): void {
    const turnstile = (this.window as WindowWithTurnstile).turnstile;
    if (!turnstile) {
      this.activeSubject?.error(new Error('Turnstile not available'));
      this.activeSubject = null;
      this.tokenInFlight$ = null;
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.widgetId = turnstile.render(`#${CONTAINER_ID}`, {
        sitekey: environment.turnstileSiteKey,
        size: 'invisible',
        retry: 'auto',
        'refresh-expired': 'auto',
        callback: (token: string) => this.onTokenReceived(token),
        'error-callback': (code?: string) => this.onError(code),
        'expired-callback': () => this.onExpired(),
        'timeout-callback': () => this.onTimeout(),
        'unsupported-callback': () => this.onUnsupported(),
      });
      this.initialized = true;
    });
  }

  private resetWidget(): void {
    const turnstile = (this.window as WindowWithTurnstile).turnstile;
    if (this.widgetId != null && turnstile) {
      this.ngZone.runOutsideAngular(() => {
        turnstile.reset(this.widgetId!);
      });
    }
  }

  private onTokenReceived(token: string): void {
    this.ngZone.run(() => {
      // Clear in-flight reference BEFORE emitting. A downstream subscriber
      // that synchronously chains another getToken() must start a fresh
      // challenge, not reuse this already-consumed (single-use) token.
      this.tokenInFlight$ = null;
      if (this.activeSubject) {
        this.activeSubject.next(token);
        this.activeSubject.complete();
        this.activeSubject = null;
      }
    });
  }

  private onError(code?: string): void {
    this.failActiveSubject(`Turnstile verification failed${code ? `: ${code}` : ''}`);
    // retry:'auto' lets Cloudflare re-run the challenge itself; calling
    // resetWidget() here on top of that can leave the widget stuck.
  }

  private onExpired(): void {
    // refresh-expired: 'auto' handles mid-session expiry. No action needed.
  }

  private onTimeout(): void {
    this.failActiveSubject('Turnstile challenge timed out');
  }

  private onUnsupported(): void {
    this.failActiveSubject('Turnstile not supported in this browser');
  }

  private failActiveSubject(message: string): void {
    this.ngZone.run(() => {
      this.tokenInFlight$ = null;
      if (!this.activeSubject) return;
      const fallback = environment.turnstileFallbackTokenOnError;
      if (fallback) {
        this.activeSubject.next(fallback);
        this.activeSubject.complete();
      } else {
        this.activeSubject.error(new Error(message));
      }
      this.activeSubject = null;
    });
  }
}
